import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import PocketModal from './PocketModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';
import { Pockets } from "../../../../types/Pockets";
const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));


const FabricPattern: React.FC = () => {
  const [pockets, setPockets] = useState<Pockets[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Pockets | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchFt = async () => {
    try {
      const data = await apiRequest<Pockets[]>({
        method: 'get',
        url: 'pockets',
      });
      setPockets(data);
    } catch (error) {
      showError('Failed to fetch fabric pattern');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchFt();
  }, []);

  const handleEditClick = (record: Pockets) => {
    setIsEditMode(true);
    setInitialValues({
      pocket_id: record.pocket_id,
      pocket_name: record.pocket_name,
      pocket_mesh_name: record.pocket_mesh_name,
      pocket_status: record.pocket_status,
      pocket_image: record.pocket_image,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchFt();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (pocket_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-pocket/${pocket_id}`,
      });
      showSuccess('Pocket deleted');
      fetchFt();
    } catch {
      showError('Failed to delete fabric pattern');
    }
  };

  const handleStatusToggle = async (pocketId: number) => {
    setPockets((prev) =>
      prev.map((item) =>
        item.pocket_id === pocketId
          ? { ...item, pocket_status: item.pocket_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-pocket/${pocketId}`,
      });
      showSuccess('Pocket status updated');
    } catch (error) {
      showError('Failed to update status');
      setPockets((prev) =>
        prev.map((item) =>
          item.pocket_id === pocketId
            ? { ...item, pocket_status: item.pocket_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Pockets>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<Pockets> = [
    {
      title: 'Pocket',
      dataIndex: 'pocket_name',
      key: 'pocket_name',
      width: 500,
      filters: Array.from(new Set(pockets.map((s) => s.pocket_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.pocket_name || null,
      onFilter: (value, record) => record.pocket_name.includes(value as string),
      sorter: (a, b) => a.pocket_name.localeCompare(b.pocket_name),
      sortOrder: sortedInfo.columnKey === 'pocket_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.pocket_name}</div>,
    },
    {
      title: 'Pocket Mesh Name',
      dataIndex: 'pocket_mesh_name',
      key: 'pocket_mesh_name',
      width: 500,
      filters: Array.from(new Set(pockets.map((s) => s.pocket_mesh_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.pocket_mesh_name || null,
      onFilter: (value, record) => record.pocket_mesh_name.includes(value as string),
      sorter: (a, b) => a.pocket_mesh_name.localeCompare(b.pocket_mesh_name),
      sortOrder: sortedInfo.columnKey === 'pocket_mesh_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.pocket_mesh_name}</div>,
    },
    {
      title: 'Image',
      dataIndex: 'pocket_image',
      key: 'pocket_image',
      width: 500,
      render: (_: any, record: any) =>
        record ? (
          <img
            src={record.pocket_image}
            alt="Pocket"
            style={{
              width: 40,
              height: 50,
              objectFit: 'cover',
              borderRadius: 8,
            }}
          />
        ) : (
          '-'
        ),

    }
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Pockets) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.pocket_status}
                onChange={() => handleStatusToggle(record.pocket_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this ft?"
              onConfirm={() => handleDelete(record.pocket_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Pocket</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Pocket
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Pockets>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="pocket_id"
          dataSource={pockets.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 13 }}
          locale={{ emptyText: 'No Pocket found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, pockets.length)} of{' '}
                {pockets.length}
              </span>
              <Pagination
                {...pagination}
                total={pockets.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <PocketModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
        pockets={pockets}
      />
    </>
  );
};

export default FabricPattern;